import { FormikValues } from 'formik';
import React, { Component, PropsWithChildren } from 'react';
import posed, { PoseGroup } from 'react-pose';
import { Modal, ModalBody, ModalHeader } from '../index';
import { classAttr } from '../Modal/utils';
import { IWizardProps } from './types';
import { IWizardMetaProps, Meta } from './utils';
import './Wizard.scss';
import { WizardCustomStep } from './WizardCustomStep';
import { WizardFormStep } from './WizardFormStep';
import { WizardSidebar } from './WizardSidebar';
import { WizardStaticStep } from './WizardStaticStep';

export interface IWizardStateProps {
  completedSteps: string[];
  currentId: string;
  isOpen: boolean;
  meta: Meta;
  transitionDirection: number;
  widthStep: number;
}

const PosedDiv = posed.div({
  enter: {
    opacity: 1,
    transition: {
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    transition: {
      ease: 'easeOut',
    },
  },
});

export class Wizard extends Component<PropsWithChildren<IWizardProps>, IWizardStateProps> {
  // e.g. expose WizardFormStep as Wizard.Form
  public static Custom = WizardCustomStep;
  public static Form = WizardFormStep;
  public static Static = WizardStaticStep;

  public currentStepRef: any;

  public state: IWizardStateProps = {
    completedSteps: [],
    currentId: '',
    isOpen: false,
    meta: new Meta(),
    transitionDirection: 0,
    widthStep: 400,
  };

  /**
   * instantiate a Meta object using the current children (Wizard.Steps)
   * @param nextProps
   * @param prevState
   */
  public static getDerivedStateFromProps(nextProps: IWizardProps, prevState: IWizardStateProps) {
    // tslint:disable-next-line
    const meta = new Meta(nextProps.children as any);
    const { currentId, isOpen } = nextProps;
    const { currentId: previousId } = prevState;
    let transitionDirection = 0;

    if (!previousId) {
      transitionDirection = 0;
    } else {
      const { index: previousIndex } = meta.props(previousId);
      const { index: currentIndex } = meta.props(nextProps.currentId);
      transitionDirection = currentIndex > previousIndex ? 1 : -1;
    }

    return {
      currentId,
      isOpen,
      meta,
      transitionDirection,
    };
  }

  // Add a step to the list of completed steps
  public completeStep = (bool: boolean = true) => {
    this.setState(({ completedSteps, currentId }) => {
      return {
        completedSteps: [currentId, ...completedSteps],
      };
    });
  };

  // returns a metadata object describing the current step
  public currentStep(): IWizardMetaProps {
    const { currentId, meta } = this.state;
    return meta.props(currentId);
  }

  public handlePrevious = () => {
    const { previousId } = this.currentStep();
    previousId && this.beforeTransition({}, previousId);
  };

  public handleClose = () => {
    const { onClose = () => '' } = this.props;
    this.setState({ isOpen: false });
    onClose();
  };

  public handleSave = () => {
    const { onSave = () => '' } = this.props;
    onSave();
  };

  public handleSelectFromSidebar = (index: number) => {
    const { completedSteps, currentId, meta } = this.state;
    const { index: currentIndex } = meta.props(currentId);

    const isNextStep = index - 1 === currentIndex;

    if (isNextStep) {
      const { handleNextButton } = this.currentStepRef;
      handleNextButton && handleNextButton();
    } else {
      // Before going to the selected step, start at the beginning and check for
      // any steps that aren't completed and stop at the first one we encounter.
      let i = 0;
      let stopped = false;
      while (i < index && !stopped) {
        if (!completedSteps.includes(meta.steps[i].props.id)) {
          stopped = true;
        } else {
          i += 1;
        }
      }

      this.beforeTransition({}, meta.steps[i].props.id);
    }
  };

  // Get a reference to the current step component so we can access child methods
  // This allows us to trigger the handleNextButton method from a sidebar selection
  // Refs to Components: https://github.com/kriasoft/react-starter-kit/issues/909#issuecomment-252969542
  public onRef = (ref: any) => {
    this.currentStepRef = ref;
  };

  public beforeTransition = (currentStepValues: FormikValues, transitionTo?: string) => {
    const { id: currentId, nextId } = this.currentStep();
    const { beforeTransition } = this.props;
    beforeTransition(currentStepValues, currentId, transitionTo || nextId);
  };

  // add class "wizard-modal" to the modal-dialog container so we can override some modal styling
  public addWizardClassToParentModal = (el: HTMLDivElement) => {
    const modal = classAttr.findAncestor(el, 'modal-dialog');
    modal && modal.classList.add('wizard-modal');
  };

  public sidebarJSON() {
    const { completedSteps, currentId, meta } = this.state;
    return meta.steps.map((step, index) => {
      const { id, sidebarLabel } = step.props;

      return {
        id,
        index,
        isActive: id === currentId,
        isComplete: completedSteps.includes(id),
        label: sidebarLabel || id,
      };
    });
  }

  public renderSteps() {
    const currentStep = this.currentStep();
    const { id: currentId, node } = currentStep;

    return (
      <div className="hlc-wizard-steps">
        <PoseGroup>
          <PosedDiv className="hlc-wizard-step-container" key={currentId}>
            {React.cloneElement(node as any, {
              _beforeTransition: this.beforeTransition,
              _isActive: true,
              _meta: currentStep,
              _onClose: this.handleClose,
              _onComplete: this.completeStep,
              _onNext: this.beforeTransition,
              _onPrevious: this.handlePrevious,
              _onRef: this.onRef,
              _onSave: this.props.onSave ? this.handleSave : null,
              _width: this.state.widthStep,
            })}
          </PosedDiv>
        </PoseGroup>
      </div>
    );
  }

  public render() {
    const { sidebarRenderer, title, size = 'fullscreen' } = this.props;

    return (
      <Modal
        size={size}
        show={this.state.isOpen}
        onHide={this.handleClose}
        draggable={false}
        keyboard={false}
        qa-id={this.props['qa-id']}
      >
        <ModalHeader title={title} closeButton={true} />
        <ModalBody>
          <div className="hlc-wizard-wrapper" ref={this.addWizardClassToParentModal}>
            <WizardSidebar
              steps={this.sidebarJSON()}
              onSelect={this.handleSelectFromSidebar}
              sidebarRenderer={sidebarRenderer}
            />
            {this.renderSteps()}
          </div>
        </ModalBody>
      </Modal>
    );
  }
}

export default Wizard;
